import { ref, Ref, computed, watch, WritableComputedRef } from "vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computedAsync } from "@vueuse/core";
import { Event, EventDrilldown } from "@/shared/model/event";
import { DrilldownNode } from "@/shared/model/drilldown-node";
import { FavouriteSportId } from "@/shared/model/favourites";
import { DRILLDOWN_LEAGUE_LEVEL_NUMBER, DRILLDOWN_SPORT_LEVEL_NUMBER } from "@/shared/utils/constants";
import {
    difference as _difference,
    intersection as _intersection,
    sortBy as _sortBy,
    uniq as _uniq,
    xor as _xor,
} from "lodash";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { LiveSportsDrilldownNode } from "@/live/model/live-sports-drilldown-nodes";
import useUtils from "@/shared/composables/use-utils";
import { EventsDrilldownDetailEventMarket } from "@/live/model/events-drilldown-detail-event";
import { MarketGroup } from "@/shared/model/market";
import { DataItem } from "@/shared/model/cms-content";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import { GroupCode } from "@/shared/model/enums/group-code";
import { getEnumKeyByEnumValue } from "@/shared/utils/enum";
import { Sport } from "@/shared/model/enums/sport";
import { setUserFavourites, getUserFavourites } from "@/api/v1-api";
import { useAuthStore } from "@/shared/stores/use-auth-store";
import { UserFavoritesResponse } from "@/api/interfaces/v1";
import { waitUntilTrue } from "@/shared/utils/wait";
import { logException } from "@/shared/utils/logger/use-app-logger";

const isSportCodeAllowed = (code: string) => ![Sport.PriceBoost].includes(code as Sport);

type FavouritesType = "sport" | "league" | "event";

export const useFavouritesStore = defineStore(
    "Favourites",
    () => {
        const authStore = useAuthStore();
        const { getEventsDrilldown, getEventsDrilldownDetail, getOutrightsEvents } = useOpenbetApi();
        const { sortByNegativeDisplayOrder, sortByDisplayOrder, normalizeText } = useUtils();
        const drilldownNodesStore = useDrilldownNodesStore();
        let cleanupIntervalId = null;

        // state
        const cleanupInterval: Ref<number> = ref(600000);
        const initialized: Ref<boolean> = ref(false);

        // persisted data in local storage
        const storedFavouriteSportIds: Ref<FavouriteSportId[]> = ref([]); // persisted
        const storedFavouriteLeagues: Ref<string[]> = ref([]); // persisted
        const storedFavouriteEvents: Ref<string[]> = ref([]); // persisted
        const storedFavouriteMarkets: Ref<string[]> = ref([]); // persisted
        // api data from server
        const apiFavouriteSportIds: Ref<FavouriteSportId[]> = ref([]);
        const apiFavouriteLeagues: Ref<string[]> = ref([]);
        const apiFavouriteEvents: Ref<string[]> = ref([]);
        const apiFavouriteMarkets: Ref<string[]> = ref([]);

        const favouriteSportIds: WritableComputedRef<FavouriteSportId[]> = computed({
            get: () => (!authStore.isLoggedIn ? storedFavouriteSportIds.value : apiFavouriteSportIds.value),
            set: (newValue) =>
                !authStore.isLoggedIn
                    ? (storedFavouriteSportIds.value = newValue)
                    : (apiFavouriteSportIds.value = newValue),
        });
        const favouriteLeagues: WritableComputedRef<string[]> = computed({
            get: () => (!authStore.isLoggedIn ? storedFavouriteLeagues.value : apiFavouriteLeagues.value),
            set: (newValue) =>
                !authStore.isLoggedIn
                    ? (storedFavouriteLeagues.value = newValue)
                    : (apiFavouriteLeagues.value = newValue),
        });
        const favouriteEvents: WritableComputedRef<string[]> = computed({
            get: () => (!authStore.isLoggedIn ? storedFavouriteEvents.value : apiFavouriteEvents.value),
            set: (newValue) =>
                !authStore.isLoggedIn
                    ? (storedFavouriteEvents.value = newValue)
                    : (apiFavouriteEvents.value = newValue),
        });
        const favouriteMarkets: WritableComputedRef<string[]> = computed({
            get: () => (!authStore.isLoggedIn ? storedFavouriteMarkets.value : apiFavouriteMarkets.value),
            set: (newValue) =>
                !authStore.isLoggedIn
                    ? (storedFavouriteMarkets.value = newValue)
                    : (apiFavouriteMarkets.value = newValue),
        });

        //persist data in local storage only when the user is not logged in
        //when the user is logged in, the data is stored in the database
        //for the switching between the local storage and the database, the initializeStorage function is used
        //it was painful and might be refactored in the future but it works rn
        const initializeStorage = async () => {
            if (authStore.isLoggedIn) {
                const response = await getUserFavourites();
                if (!isResponseUserFavourites(response)) {
                    addAutomaticFavouriteSports();
                    return;
                }
                apiFavouriteSportIds.value = response.sports;
                apiFavouriteLeagues.value = response.leagues;
                apiFavouriteEvents.value = response.events;
                apiFavouriteMarkets.value = response.markets;
                favouritesStoreCleanup();
                saveUserFavourites();
            } else {
                if (favouriteSportIds.value.length === 0) {
                    addAutomaticFavouriteSports();
                }
                initialized.value = true;
            }
            initialized.value = true;
        };
        // getters
        const sportIdsFromFavouriteLeagues = computedAsync(async () => {
            const sports = await favouriteLeagues.value.mapAsync(async (leagueId) =>
                (await drilldownNodesStore.getDrilldownNodesById(leagueId))?.find(
                    (node) => node.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER,
                ),
            );
            const sportsSorted = _sortBy(sports, ["displayOrder", "name"]);
            return _uniq(sportsSorted?.map((sport) => sport.id));
        });
        const favouriteSports = computed(() => {
            return favouriteSportIds.value.filter((sport) => sport.present).map((sport) => sport.id);
        });
        const isFavouriteSport = computed(() => {
            return (sportId: string) => favouriteSports.value.includes(sportId);
        });
        const isFavouriteLeague = computed(() => {
            return (leagueId: string) => favouriteLeagues.value.includes(leagueId);
        });
        const isFavouriteEvent = computed(() => {
            return (eventId: string) => favouriteEvents.value.includes(eventId);
        });
        const isFavouriteMarket = computed(() => {
            return (marketId: string) => favouriteMarkets.value.includes(marketId);
        });
        const hasFavouriteEvents = computed(() => {
            return favouriteEvents.value.length > 0;
        });
        const hasFavouriteLeagues = computed(() => {
            return favouriteLeagues.value.length > 0;
        });
        const hasFavouriteEventsOrLeagues = computed(() => hasFavouriteEvents.value || hasFavouriteLeagues.value);
        const isEventInFavouriteEventsOrLeagues = computed(() => {
            return (event: EventDrilldown) =>
                favouriteEvents.value.includes(event.id) ||
                favouriteLeagues.value.includes(
                    event.drilldownNodes?.find(
                        (node: DrilldownNode) => node.levelNumber === DRILLDOWN_LEAGUE_LEVEL_NUMBER,
                    )?.id,
                );
        });
        const displayableFavouritesCount = computed(() => {
            return (leagueIds: string[], eventIds: string[]) =>
                _intersection(favouriteLeagues.value, leagueIds || []).length +
                _intersection(favouriteEvents.value, eventIds || []).length;
        });
        const automaticFavouriteSports = computed(() => {
            const nodes = drilldownNodesStore.sports.filter((d) => d.displayOrder < 0 && isSportCodeAllowed(d.code));
            return _sortBy(nodes, ["displayOrder", "name"]);
        });

        // actions
        const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
            await waitUntilTrue(initialized, time, timeout).catch(() => {
                logException("favourites", undefined, {
                    message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
                });
            });
        };
        const getSportsAll = (): DrilldownNode[] => {
            favouriteSportIdsValidation();
            return drilldownNodesStore.sports.filter((s) => isSportCodeAllowed(s.code));
        };
        const favouriteSportIdsValidation = () => {
            if (!favouriteSportIds.value.length) return;
            // Validate whether ids in favouriteSportIds still exists and if not delete all non-existent ids in favouriteSportIds
            const allAvailableIds = drilldownNodesStore.sports.map((sport) => sport.id);
            const nonExistentIdsInFavourites =
                allAvailableIds.length > 0
                    ? _difference(
                          favouriteSportIds.value.map((sport) => sport.id),
                          allAvailableIds,
                      )
                    : [];
            nonExistentIdsInFavourites.forEach((sportId) => {
                favouriteSportIds.value = favouriteSportIds.value.filter((sport) => sport.id !== sportId);
            });
        };
        const favouriteLeaguesValidation = () => {
            if (!favouriteLeagues.value.length) return;
            // Fetch fresh data, so we are sure that drilldown nodes are up-to-date
            drilldownNodesStore.fetchDrilldownNodes().then(() => {
                const leagueIds = drilldownNodesStore.leagues.map((league) => league.id);
                favouriteLeagues.value = favouriteLeagues.value.filter((id) => leagueIds.includes(id));
            });
            // Checking if league exists by comparing with returned drilldown node ids is sometimes not enough, and we need to check whether league contains some events/outrights as well
            if (favouriteLeagues.value.length) {
                Promise.allSettled([
                    getEventsDrilldown({ drilldownTagIds: favouriteLeagues.value }).then((response) =>
                        response.map((event) => event.competitionDrilldownTagId),
                    ),
                    getOutrightsEvents({ drilldownTagIds: favouriteLeagues.value }).then((response) =>
                        response.map((event) => event.competitionDrilldownTagId),
                    ),
                ]).then((results) => {
                    let eventAndOutrightLeagueIds = [];
                    results.forEach((result) => {
                        if (result.status === "fulfilled") {
                            eventAndOutrightLeagueIds = eventAndOutrightLeagueIds.concat(
                                (result as PromiseFulfilledResult<string[]>).value,
                            );
                        }
                    });
                    const eventAndOutrightLeagueIdsFiltered = new Set(eventAndOutrightLeagueIds);
                    favouriteLeagues.value = favouriteLeagues.value.filter((id) =>
                        eventAndOutrightLeagueIdsFiltered.has(id),
                    );
                });
            }
        };
        const favouriteEventsValidation = () => {
            if (!favouriteEvents.value.length) return;
            // If event is not returned from endpoint by its id we declare it as non-existent and should be safe to delete (detail endpoint is used here to get event long term bets/outrights)
            getEventsDrilldownDetail({ eventIds: favouriteEvents.value }).then((response) => {
                const responseEventIds = response.map((event) => event.id);
                favouriteEvents.value = favouriteEvents.value.filter((id) => responseEventIds.includes(id));
            });
        };
        const favouriteMarketsValidation = () => {
            if (!favouriteMarkets.value.length) return;
            const marketIds: string[] = [];
            const marketGroupIds: { eventId: string; groupCode: GroupCode }[] = [];
            favouriteMarkets.value.forEach((id) => {
                const idSplit: string[] = id.split("-");
                if (idSplit.length === 2) {
                    marketGroupIds.push({
                        eventId: idSplit[0],
                        groupCode: GroupCode[getEnumKeyByEnumValue(GroupCode, idSplit[1])],
                    });
                } else {
                    marketIds.push(idSplit[0]);
                }
            });
            // Fetch events by their market ids and collect their current market ids... if not returned we declare it as non-existent
            if (marketIds.length > 0) {
                getEventsDrilldownDetail({ marketIds: marketIds }).then((response) => {
                    const responseMarketIds = response.flatMap((event) => event.markets).map((market) => market.id);
                    favouriteMarkets.value = favouriteMarkets.value.filter((id) => {
                        const idSplit: string[] = id.split("-");
                        if (idSplit.length === 2) {
                            return true;
                        } else {
                            return responseMarketIds.includes(idSplit[0]);
                        }
                    });
                });
            }
            // Fetch events by their market ids... if not returned we declare it as non-existent
            // There is no better way than check the existence of the event only,
            // otherwise we would need to group their markets (resource exhaustive) and then check existence of the market group as well
            if (marketGroupIds.length > 0) {
                getEventsDrilldownDetail({ eventIds: marketGroupIds.map((group) => group.eventId) }).then(
                    (response) => {
                        const responseEventIds = response.map((event) => event.id);
                        favouriteMarkets.value = favouriteMarkets.value.filter((id) => {
                            const idSplit: string[] = id.split("-");
                            if (idSplit.length === 2) {
                                return responseEventIds.includes(idSplit[0]);
                            } else {
                                return true;
                            }
                        });
                    },
                );
            }
        };
        const favouritesStoreCleanup = () => {
            // Disclaimer: Cleanup is heavily dependent od data provided by endpoints.
            // In case that some endpoint will not return correct/expected data at the time of cleanup user can expect some unwelcome/unexpected behavior
            // like deletion of favourite live event even if it is still in progress but not returned by endpoint for unknown number of requests
            // for unknown reason (typical for Esports for example) and other unmapped discrepancies.
            favouriteMarketsValidation();
            favouriteEventsValidation();
            favouriteLeaguesValidation();
            favouriteSportIdsValidation();
        };
        const addAutomaticFavouriteSports = () => {
            // Automatically add ids with displayOrder < 0 (if not previously removed by user)
            const automaticallyAddedIds = automaticFavouriteSports.value.map((node) => node.id);
            const idsToAdd = _difference(
                automaticallyAddedIds,
                favouriteSportIds.value.map((sport) => sport.id),
            );
            // Add in reversed order so items with the highest negative displayOrder values are first
            const alreadyRemovedIdsByUser = favouriteSportIds.value
                .filter((i) => !i.present && i.manual)
                .map((i) => i.id);
            for (let i = idsToAdd.length - 1; i >= 0; i--) {
                // Check whether it wasn't removed by user previously
                if (!alreadyRemovedIdsByUser.includes(idsToAdd[i])) {
                    toggleFavouriteSport(idsToAdd[i], false, true);
                }
            }
        };
        const saveFavouriteSports = ([...sportIds]: string[]) => {
            // Toggle the favourite sports first
            const changes = _xor(favouriteSports.value, sportIds);
            changes.forEach((id) => toggleFavouriteSport(id));
            // Change order by order of sportIds (order matters here)
            favouriteSportIds.value = _sortBy(favouriteSportIds.value, (sport) => sportIds.indexOf(sport.id));
            saveUserFavourites();
        };
        const toggleFavouriteSport = (sportId: string, manual: boolean = true, first: boolean = false) => {
            const curSport = favouriteSportIds.value.find((sport) => sport.id === sportId);
            if (curSport) {
                curSport.present = !curSport.present;
                curSport.manual = manual;
            } else {
                const newSport = { id: sportId, present: true, manual };
                favouriteSportIds.value = first
                    ? [newSport, ...favouriteSportIds.value]
                    : [...favouriteSportIds.value, newSport];
            }
        };
        const toggleFavouriteLeague = (leagueId: string) => {
            if (favouriteLeagues.value.includes(leagueId)) {
                favouriteLeagues.value = favouriteLeagues.value.filter((id) => id !== leagueId);
            } else {
                favouriteLeagues.value = [...favouriteLeagues.value, leagueId];
            }
            saveUserFavourites();
        };
        const toggleFavouriteEvent = (eventId: string) => {
            if (favouriteEvents.value.includes(eventId)) {
                favouriteEvents.value = favouriteEvents.value.filter((id) => id !== eventId);
            } else {
                favouriteEvents.value = [...favouriteEvents.value, eventId];
            }

            saveUserFavourites();
        };
        const toggleFavouriteMarket = (marketId: string) => {
            if (favouriteMarkets.value.includes(marketId)) {
                favouriteMarkets.value = favouriteMarkets.value.filter((id) => id !== marketId);
            } else {
                favouriteMarkets.value = [...favouriteMarkets.value, marketId];
            }
            saveUserFavourites();
        };
        const setCleanupInterval = (interval: number) => {
            cleanupInterval.value = interval;
        };

        const saveUserFavourites = async () => {
            if (!authStore.isLoggedIn) return;
            await setUserFavourites({
                sports: apiFavouriteSportIds.value,
                leagues: apiFavouriteLeagues.value,
                events: apiFavouriteEvents.value,
                markets: apiFavouriteMarkets.value,
            });
        };

        const isResponseUserFavourites = (response: unknown): response is UserFavoritesResponse => {
            return (
                typeof response === "object" &&
                response !== null &&
                "sports" in response &&
                "leagues" in response &&
                "events" in response &&
                "markets" in response
            );
        };

        watch(() => authStore.isLoggedIn, initializeStorage, { immediate: true });

        // additional logic
        watch(
            () => automaticFavouriteSports.value,
            (newValue, oldValue) => {
                if (
                    Array.isArray(newValue) &&
                    Array.isArray(oldValue) &&
                    (newValue.length !== oldValue.length ||
                        _xor(
                            newValue.map((node) => node.id),
                            oldValue.map((node) => node.id),
                        ).length > 0)
                ) {
                    // Add automatic favourite sports on store initialization (maintained by oldValue.length 0 value) and in case that automaticFavouriteSports changes
                    addAutomaticFavouriteSports();
                }
            },
        );

        // cleanup the favourites store on initialization and in the given time interval
        favouritesStoreCleanup();
        watch(
            cleanupInterval,
            (newValue) => {
                if (newValue) {
                    cleanupIntervalId = setInterval(() => {
                        favouritesStoreCleanup();
                    }, cleanupInterval.value);
                } else if (cleanupIntervalId) {
                    clearInterval(cleanupIntervalId);
                }
            },
            { immediate: true },
        );

        // sorting helpers
        const sortByFavourites = <T extends { id: string; name: string; displayOrder: number }>(
            items: T[],
            type: FavouritesType,
            useNegativeDisplayOrder: boolean = true,
        ): T[] => {
            let sortByArray: string[] = [];
            switch (type) {
                case "sport":
                    sortByArray = favouriteSports.value;
                    break;
                case "league":
                    sortByArray = favouriteLeagues.value;
                    break;
                case "event":
                    sortByArray = favouriteEvents.value;
                    break;
            }
            if (!useNegativeDisplayOrder) {
                return _sortBy(sortByDisplayOrder(items), (i) => -[...sortByArray].reverse().indexOf(i.id));
            }
            return _sortBy(sortByNegativeDisplayOrder(items), (i) => -[...sortByArray].reverse().indexOf(i.id));
        };
        const sortEvents = <T extends Event>(events: T[]): T[] => {
            return sortByFavourites(events, "event", false);
        };
        const sortLiveSportsDrilldownNodes = (liveSports: LiveSportsDrilldownNode[]): LiveSportsDrilldownNode[] => {
            return sortByFavourites(liveSports, "sport");
        };
        const sortDrilldownNodes = <T extends DrilldownNode>(drilldownNodes: T[], type: FavouritesType): T[] => {
            return sortByFavourites(drilldownNodes, type);
        };
        const sortMarkets = (markets: Array<EventsDrilldownDetailEventMarket | MarketGroup>) => {
            return _sortBy(
                _sortBy(markets, ["displayOrder", "name"]),
                (i) =>
                    -[...favouriteMarkets.value]
                        .reverse()
                        .indexOf(i.id ?? `${i.eventId}-${normalizeText(i.marketName)}`),
            );
        };
        const sortTopLeagues = (topLeagues: DataItem[]): DataItem[] => {
            return _sortBy(
                topLeagues,
                (i) => -[...favouriteLeagues.value].reverse().indexOf(i.navigation.competitionId),
            );
        };

        return {
            // state
            initialized,
            favouriteSportIds,
            favouriteLeagues,
            favouriteEvents,
            // getters
            sportIdsFromFavouriteLeagues,
            favouriteSports,
            isFavouriteSport,
            isFavouriteLeague,
            isFavouriteEvent,
            isFavouriteMarket,
            hasFavouriteEvents,
            hasFavouriteLeagues,
            hasFavouriteEventsOrLeagues,
            isEventInFavouriteEventsOrLeagues,
            displayableFavouritesCount,
            automaticFavouriteSports,
            // actions
            waitUntilInitialized,
            getSportsAll,
            favouriteSportIdsValidation,
            favouriteLeaguesValidation,
            favouriteEventsValidation,
            favouriteMarketsValidation,
            favouritesStoreCleanup,
            addAutomaticFavouriteSports,
            saveFavouriteSports,
            toggleFavouriteSport,
            toggleFavouriteLeague,
            toggleFavouriteEvent,
            toggleFavouriteMarket,
            setCleanupInterval,
            sortByFavourites,
            sortEvents,
            sortLiveSportsDrilldownNodes,
            sortDrilldownNodes,
            sortMarkets,
            sortTopLeagues,
            // to be persisted, it needs to be exported
            storedFavouriteSportIds,
            storedFavouriteLeagues,
            storedFavouriteEvents,
            storedFavouriteMarkets,
        };
    },
    {
        persist: {
            pick: [
                "storedFavouriteSportIds",
                "storedFavouriteLeagues",
                "storedFavouriteEvents",
                "storedFavouriteMarkets",
            ],
            storage: localStorage,
        },
    },
);

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFavouritesStore, import.meta.hot));
}
